/**
 * Bundles (Appstle Combo Bundle) – DOM tweaks.
 *
 * The third-party widget renders a <span> sibling next to
 * `div.mb_pro_bundle_btn` with an inline `display: flex !important;`.
 * We want that span hidden, but CSS can't beat inline !important, and the
 * widget re-applies the style on re-render, so we use a MutationObserver
 * to keep it hidden.
 *
 * NOTE: Appstle injects `.mb_pro_bundle_btn` into an unpredictable DOM
 * location (not necessarily inside our own `.bundles` / `.appstle-combo-bundle`
 * container), so we have to observe `document.body`. To keep the cost low
 * we filter mutations aggressively before doing any work.
 */

const SIBLING_SELECTOR = '.mb_pro_bundle_btn';

function hideSpanNextTo(btn) {
    const span = btn.nextElementSibling;
    if (!span || span.tagName !== 'SPAN') return;
    if (span.style.display !== 'none') {
        span.style.setProperty('display', 'none', 'important');
    }
}

function scan(root = document) {
    root.querySelectorAll(SIBLING_SELECTOR).forEach(hideSpanNextTo);
}

let observer = null;

function handleMutations(mutations) {
    for (const mutation of mutations) {
        // The widget re-applied the inline style on a target SPAN – re-hide
        // it directly without a full re-scan.
        if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'style' &&
            mutation.target.tagName === 'SPAN'
        ) {
            const prev = mutation.target.previousElementSibling;
            if (prev && prev.matches && prev.matches(SIBLING_SELECTOR)) {
                hideSpanNextTo(prev);
            }
            continue;
        }

        // New nodes added: only re-scan if the added subtree could plausibly
        // contain a bundle button (skip text nodes and unrelated elements).
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== 1) continue;
                if (
                    node.matches?.(SIBLING_SELECTOR) ||
                    node.querySelector?.(SIBLING_SELECTOR)
                ) {
                    scan(node.parentElement || document);
                    break;
                }
            }
        }
    }
}

function init() {
    scan();

    if (observer) return;
    observer = new MutationObserver(handleMutations);
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

// Re-scan when Shopify re-renders the section in the theme editor.
document.addEventListener('shopify:section:load', () => scan());
